import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./WrappingSelect.scss";

// Revert: delete this file and use SelectInput again for Address Proof.
const toOption = (opt) =>
  typeof opt === "string"
    ? { label: opt, value: opt }
    : { label: opt?.label ?? "", value: opt?.value ?? "" };

const menuPosition = (button) => {
  const rect = button.getBoundingClientRect();
  const viewportWidth = window.innerWidth || 1024;
  const viewportHeight = window.innerHeight || 768;
  const width = Math.min(
    Math.max(rect.width || 280, 280),
    560,
    Math.max(160, viewportWidth - 24),
  );
  let left = rect.left || 12;
  if (left + width > viewportWidth - 12) {
    left = viewportWidth - 12 - width;
  }
  if (left < 12) left = 12;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
  const maxHeight = Math.max(
    120,
    Math.min(320, (openUp ? spaceAbove : spaceBelow) - 16),
  );

  return {
    position: "fixed",
    left,
    width,
    maxHeight,
    zIndex: 10000,
    top: openUp ? undefined : rect.bottom + 4,
    bottom: openUp ? viewportHeight - rect.top + 4 : undefined,
  };
};

export default function WrappingSelect({
  placeholder = "Select",
  value,
  onChange,
  name,
  options,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();
  const items = (options || []).map(toOption);
  const selected = items.find(
    (opt) => String(opt.value) === String(value ?? ""),
  );

  useEffect(() => {
    if (!open) return undefined;

    const place = () => {
      if (buttonRef.current) setMenuStyle(menuPosition(buttonRef.current));
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const choose = (next) => {
    onChange(name, next);
    setOpen(false);
  };

  return (
    <div
      className={`wrapping-select${disabled ? " is-disabled" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        ref={buttonRef}
        className="wrapping-select__button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        title={selected?.label || placeholder}
        onClick={() => {
          if (disabled) return;
          if (!open && buttonRef.current) {
            setMenuStyle(menuPosition(buttonRef.current));
          }
          setOpen((current) => !current);
        }}
      >
        <span className={selected ? undefined : "is-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
      </button>
      {open &&
        menuStyle &&
        createPortal(
          <ul
            id={listId}
            role="listbox"
            className="wrapping-select__menu"
            style={menuStyle}
            ref={menuRef}
          >
            <li>
              <button
                type="button"
                role="option"
                aria-selected={!selected}
                className="wrapping-select__option"
                onClick={() => choose("")}
              >
                {placeholder}
              </button>
            </li>
            {items.map((opt, index) => {
              const isSelected = String(opt.value) === String(value ?? "");
              return (
                <li key={`${String(opt.value)}-${index}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`wrapping-select__option${
                      isSelected ? " is-selected" : ""
                    }`}
                    onClick={() => choose(String(opt.value))}
                  >
                    {opt.label}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
